import _ from 'lodash';
import { createSelector } from 'reselect';
import { offsetToUtc } from '../../../../../shared/frontend/libs/utils';
import { TApplicationState, TRecruitmentProfile } from '../libs/store';
import {
  claimedCharactersSelector,
  claimedGuildsSelector,
  classesSelector
} from '../../../../../shared/frontend/scripts/selectors/global';
import { ownProfilesSelector } from '../shared/recruitment/selectors';
import { TProfileCharacter as TSavedCharacterProfile } from '../../../../../shared/types/recruitmentTypes';
import { RecruitmentEntityType } from '../../../../../shared/types/recruitmentTypes';
import { RecruitmentConfigSteps } from '../../../../../shared/frontend/scripts/recruitment/constants';
import { isProfileComplete, profileStepValidators } from './utils';
import { getPermissionsForRoles } from '../../../../../shared/common/group';
import { adjustScheduleArrayForTimezone } from '../../../../../shared/common/recruitment';

const recruitmentStateSelector = (state: Partial<TApplicationState>) => state.createRecruitmentProfile;

export const recruitmentProfileTypeSelector = createSelector(
  recruitmentStateSelector,
  ({ profileType }) => profileType
);

const _authStateSelector = (st: Partial<TApplicationState>) => st.auth || {};
const _authGuildSelector = createSelector(claimedGuildsSelector, (memberGuildGroups) =>
  _.reduce(
    memberGuildGroups,
    (guilds: any[], cg) => {
      const permissions = getPermissionsForRoles(cg.roles.map((r: any) => r.role));
      if (permissions.canManageRecruitment) {
        guilds.push(cg.guild);
      }
      return guilds;
    },
    []
  )
);

const _authTeamsSelector = createSelector(_authStateSelector, (state) => {
  const { memberGroupCharters } = state;
  return _.reduce(
    memberGroupCharters || [],
    (groupCharters: any[], cg) => {
      const permissions = getPermissionsForRoles(cg.roles.map((r: any) => r.role));
      if (permissions.canManageRecruitment) {
        groupCharters.push(cg.groupCharter);
      }
      return groupCharters;
    },
    []
  );
});

const DEFAULT_STEPS = {
  [RecruitmentEntityType.Character]: RecruitmentConfigSteps.CharacterSlots,
  [RecruitmentEntityType.Team]: RecruitmentConfigSteps.PlayerSlots,
  [RecruitmentEntityType.Guild]: RecruitmentConfigSteps.PlayerSlots
};
// todo: pull entire step object so next/prev can be used from there
export const activeStepSelector = createSelector(
  recruitmentStateSelector,
  ({ activeStep, profileType }) => activeStep || DEFAULT_STEPS[profileType as RecruitmentEntityType]
);

// retrieve full profile target object
// the character/guild/team that the recruitment profile is being created for;
export const profileTargetSelector = createSelector(
  recruitmentStateSelector,
  claimedCharactersSelector,
  _authGuildSelector,
  _authTeamsSelector,
  ({ id, profileType, ...rest }, character, guild, teams) => {
    if (profileType === RecruitmentEntityType.Character) {
      const match = _.find(character, ({ details }) => details?.persona_id === Number(id));
      if (match) {
        return match;
      }

      // Fallback: use character data from the loaded profile when the auth state
      // doesn't include the character (e.g. filtered out by level cap)
      const profileState = rest.characterProfile;
      const mainChar = _.find(profileState?.characters, { ordinal: 0 });
      if (mainChar?.character) {
        return { name: mainChar.character.name, details: { persona_id: mainChar.persona_id, ...mainChar.character } };
      }
    } else if (profileType === RecruitmentEntityType.Guild) {
      const match = _.find(guild, (g) => g.id === Number(id));
      if (match) {
        return match;
      }

      // Fallback: use guild data from the loaded profile
      const profileState = rest.guildProfile;
      if (profileState?.guild?.guild) {
        return profileState.guild.guild;
      }
    } else if (profileType === RecruitmentEntityType.Team) {
      const match = _.find(teams, ({ charter_id }) => charter_id === Number(id));
      if (match) {
        return match;
      }

      // Fallback: use team data from the loaded profile
      const profileState = rest.mplus_teamProfile;
      if (profileState?.groupCharter) {
        return { ...profileState.groupCharter.groupCharter, group_id: profileState.groupCharter.group_id };
      }
    }
  }
);

// retrieves the profile types(activity) configured so far for the given
const targetIdPaths: { [key: string]: string } = {
  [RecruitmentEntityType.Character]: 'details.persona_id',
  [RecruitmentEntityType.Guild]: 'id',
  [RecruitmentEntityType.Team]: 'group_id'
};
const profileEntityIdPaths: { [key: string]: string } = {
  [RecruitmentEntityType.Character]: 'characters[0].persona_id',
  [RecruitmentEntityType.Guild]: 'guild.guild.id',
  [RecruitmentEntityType.Team]: 'groupCharter.group_id'
};

export const configuredActivitesSelector = createSelector(
  recruitmentProfileTypeSelector,
  profileTargetSelector,
  ownProfilesSelector,
  (profileType, target, existingProfiles) => {
    const targetId = _.get(target, targetIdPaths[profileType]);

    const configuredForTarget = _.filter(existingProfiles, (p) => {
      const entityId = _.get(p, profileEntityIdPaths[profileType]);
      return entityId === targetId;
    });

    return _.map(configuredForTarget, (p) => p.activity_type);
  }
);

export const recruitmentProfileSelector = createSelector(
  recruitmentStateSelector,
  ({ profileType, ...createProfileState }) => {
    const profileState = createProfileState[`${profileType}Profile`];
    return profileState;
  }
);

export type TStep = {
  label: string;
  name: string;
  icon: string;
  isStepComplete: (prof: TRecruitmentProfile, errors: any[]) => boolean;
  nextStep?: string;
  prevStep?: string;
};

type TStepsList = {
  [key: string]: TStep[];
};
type TNavigationSteps = {
  prevStep?: RecruitmentConfigSteps;
  nextStep?: RecruitmentConfigSteps;
};
const BASE_STEPS: { [key: string]: (nav: TNavigationSteps) => TStep } = {
  slots: (navigation) => ({
    label: 'recruitment:editor.nav.player-slots',
    name: RecruitmentConfigSteps.PlayerSlots,
    icon: 'fa-user-plus',
    isStepComplete: (profile: any, errors: any[]): boolean => profileStepValidators.playerSlots(profile, errors),
    ...navigation
  }),
  requirements: (navigation) => ({
    label: 'recruitment:editor.nav.requirements',
    name: RecruitmentConfigSteps.Requirements,
    icon: 'fa-clipboard-check',
    isStepComplete: (profile: any, errors: any[]): boolean => profileStepValidators.requirements(profile, errors),
    ...navigation
  }),
  schedule: (navigation: TNavigationSteps) => ({
    label: 'recruitment:editor.nav.schedule',
    name: RecruitmentConfigSteps.Schedule,
    icon: 'fa-calendar',
    isStepComplete: (profile: any, errors: any[]): boolean => profileStepValidators.schedule(profile, errors),
    ...navigation
  }),
  publish: (navigation: TNavigationSteps) => ({
    label: 'recruitment:editor.nav.publish',
    name: RecruitmentConfigSteps.Publish,
    icon: 'fa-address-card',
    isStepComplete: (profile: any, errors: any[]): boolean => profileStepValidators.publish(profile, errors),
    ...navigation
  }),
  charSlots: (navigation: TNavigationSteps) => ({
    label: 'recruitment:editor.nav.character-slots',
    name: RecruitmentConfigSteps.CharacterSlots,
    icon: 'fa-user-plus',
    isStepComplete: (profile: any, errors: any[]): boolean => profileStepValidators.characterSlots(profile, errors),
    ...navigation
  })
};

const STEPS: TStepsList = {
  [RecruitmentEntityType.Character]: [
    BASE_STEPS.charSlots({ nextStep: RecruitmentConfigSteps.Schedule }),
    BASE_STEPS.schedule({
      nextStep: RecruitmentConfigSteps.Requirements,
      prevStep: RecruitmentConfigSteps.CharacterSlots
    }),
    BASE_STEPS.requirements({ nextStep: RecruitmentConfigSteps.Publish, prevStep: RecruitmentConfigSteps.Schedule }),
    BASE_STEPS.publish({ prevStep: RecruitmentConfigSteps.Requirements })
  ],
  [RecruitmentEntityType.Guild]: [
    BASE_STEPS.slots({ nextStep: RecruitmentConfigSteps.Schedule }),
    BASE_STEPS.schedule({
      nextStep: RecruitmentConfigSteps.Requirements,
      prevStep: RecruitmentConfigSteps.PlayerSlots
    }),
    BASE_STEPS.requirements({ nextStep: RecruitmentConfigSteps.Publish, prevStep: RecruitmentConfigSteps.Schedule }),
    BASE_STEPS.publish({ prevStep: RecruitmentConfigSteps.Requirements })
  ],
  [RecruitmentEntityType.Team]: [
    BASE_STEPS.slots({ nextStep: RecruitmentConfigSteps.Schedule }),
    BASE_STEPS.schedule({
      nextStep: RecruitmentConfigSteps.Requirements,
      prevStep: RecruitmentConfigSteps.PlayerSlots
    }),
    BASE_STEPS.requirements({ nextStep: RecruitmentConfigSteps.Publish, prevStep: RecruitmentConfigSteps.Schedule }),
    BASE_STEPS.publish({ prevStep: RecruitmentConfigSteps.Requirements })
  ]
};

export const profileCreationStepsSelector = createSelector(
  recruitmentStateSelector,
  recruitmentProfileSelector,
  ({ profileType, activeStep }, profileState) => STEPS[profileType]
);

export const availableSecondaryCharactersSelector = createSelector(
  claimedCharactersSelector,
  recruitmentProfileSelector,
  (characters, profile) => {
    if (!profile) {
      return characters;
    }
    const selectedCharacterIds = _.map(profile.characters, (c) => Number(c.persona_id));
    return _.filter(characters, ({ details }) => !selectedCharacterIds.includes(details.persona_id));
  }
);

const profileCategories = ['tags', 'characters', 'schedule', 'requirements', 'slots'];
export const recruitmentProfilePostBodySelector = createSelector(
  recruitmentProfileTypeSelector,
  recruitmentProfileSelector,
  recruitmentStateSelector,
  (type, profile, { profileId: id }): TSavedCharacterProfile => ({
    id,
    profile: _.omit(profile, profileCategories),
    ..._.pick(profile, profileCategories),

    // this is called before saving a profile to the server, where we generate a clone
    // of the schedule data, but we want to move it into UTC because that's what the
    // server needs to store it as
    schedule: adjustScheduleArrayForTimezone(profile.schedule, offsetToUtc(profile.schedule_timezone))
  })
);

export const RECRUITMENT_DROPDOWN_ROLES = [
  { id: 13, name: 'recruitment:roles.any-tank', slug: 'tank' },
  { id: 14, name: 'recruitment:roles.any-healer', slug: 'healer' },
  { id: 15, name: 'recruitment:roles.any-dps', slug: 'dps' },
  { id: 16, name: 'recruitment:roles.any-ranged-dps', slug: 'dps-ranged' },
  { id: 17, name: 'recruitment:roles.any-melee-dps', slug: 'dps-melee' },
  { id: 18, name: 'recruitment:roles.any-role', slug: 'any' }
];

// note: this is also used on the guild recruitment page, as its needed for the
// recruitment slot list.
// Consider moving to selectors/global or creating a selectors file for the shared.
// It doesn't really belong in global, as it is specific to recruitment.
//
// Creating a selectors file in shared/recruitment
//
// or generating this list in the guild slot card
export const guildRecruitmentOptionsSelector = createSelector(classesSelector, (classes) => {
  return classes.concat(RECRUITMENT_DROPDOWN_ROLES);
});

export const profileConfigStatusStateSelector = createSelector(
  recruitmentStateSelector,
  ({ loading, loaded, error, errorMessage }) => ({ loading, loaded, error, errorMessage })
);
export const saveProfileStatusSelector = createSelector(recruitmentStateSelector, ({ saveStatus }) => ({
  loading: saveStatus.loading,
  loaded: saveStatus.loaded,
  error: saveStatus.error,
  saveType: saveStatus.saveType
}));
export const profileIdSelector = createSelector(recruitmentStateSelector, ({ profileId }) => profileId);

export const tagsSelector = createSelector(recruitmentStateSelector, ({ tags }) => tags);
export const tagsForProfileSelector = createSelector(
  recruitmentStateSelector,
  recruitmentProfilePostBodySelector,
  ({ tags }, { profile }) => {
    const { activity_type, entity_type } = profile;
    const activityKey = `supports_activity_${activity_type}`;
    const entityKey = `supports_entity_${entity_type}`;
    // since the api returns tags based on a profile ID, the api should probably do this
    // filtering instead?
    return _.reduce(
      tags,
      (acc: any[], cur) => {
        const validTag = cur[activityKey] && cur[entityKey];
        if (validTag) {
          acc.push({
            ...cur,
            label: cur.name,
            value: cur.slug
          });
        }
        return acc;
      },
      []
    );
  }
);

export const requirementsSelector = createSelector(
  recruitmentStateSelector,
  recruitmentProfileSelector,
  ({ requirements }, profile) => {
    if (!profile) {
      return [];
    }
    const { activity_type, entity_type } = profile;
    const activityKey = `supports_activity_${activity_type}`;
    const entityKey = `supports_entity_${entity_type}`;
    const configuredReqs = _.groupBy(profile.requirements, 'id');
    return _.reduce(
      requirements,
      (acc: any[], cur) => {
        const validReq = cur[activityKey] && cur[entityKey] && !configuredReqs[cur.id];
        if (validReq) {
          acc.push({ ...cur, label: cur.name, value: cur.id });
        }
        return acc;
      },
      []
    );
  }
);

export const newProfileRequestBodySelector = createSelector(
  recruitmentStateSelector,
  ({ profileType, id, ...profState }) => {
    const { activity_type } = profState[`${profileType}Profile`];
    return {
      type: profileType,
      activity: activity_type,
      target_id: id
    };
  }
);

// determines whether or not a profile is deemed complete and can be published or not
// guild/team have the same validation method for now, but keeping them separate
// to easily expand/handle entity specific validations in the future
export const profileIsCompleteSelector = createSelector(recruitmentProfileSelector, (profile) =>
  isProfileComplete(profile)
);

export const errorMessageSelector = createSelector(recruitmentStateSelector, ({ errorMessage }) => errorMessage);

export const userLanguageSelector = createSelector(
  recruitmentStateSelector,
  ({ userProfile }) => userProfile?.language
);

/*
 * All VALID entities for recruitment, with added existing profileTypes for
 * the given entity
 */
export const recruitmentEntitiesSelector = createSelector(
  claimedCharactersSelector,
  _authGuildSelector,
  _authTeamsSelector,
  ownProfilesSelector,
  (characters, guilds, teams, existingProfiles) => {
    return {
      characters: _.map(characters, (char) => {
        const existingProfileTypes = _.reduce(
          existingProfiles,
          (profileTypes: string[], profile) => {
            if (profile.entity_type === RecruitmentEntityType.Character) {
              if (profile.characters?.length > 0) {
                const mainChar = _.find(profile.characters, { ordinal: 0 });
                if (mainChar.persona_id === char.details.persona_id) {
                  profileTypes.push(profile.activity_type);
                }
              }
            }
            return profileTypes;
          },
          []
        );
        return { ...char, existingProfileTypes };
      }),
      guilds: _.map(guilds, (guild) => {
        const existingProfileTypes = _.reduce(
          existingProfiles,
          (profileTypes: string[], profile) => {
            if (profile.entity_type === RecruitmentEntityType.Guild) {
              if (profile.guild.guild.id === guild.id) {
                profileTypes.push(profile.activity_type);
              }
            }
            return profileTypes;
          },
          []
        );
        return { ...guild, existingProfileTypes };
      }),
      teams: _.map(teams, (team) => {
        const existingProfileTypes = _.reduce(
          existingProfiles,
          (profileTypes: string[], profile) => {
            if (profile.entity_type === RecruitmentEntityType.Team) {
              if (profile.groupCharter.group_id === team.group_id) {
                profileTypes.push(profile.activity_type);
              }
            }
            return profileTypes;
          },
          []
        );
        return { ...team, existingProfileTypes };
      })
    };
  }
);
